"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseBigIntToken } from "@/lib/tool-kit/math/bigint";
import {
  buildFactorTree,
  divisionLadder,
  divisorCount,
  expandedForm,
  exponentForm,
  type Factorization,
  factorize,
  listDivisors,
  MAX_FACTOR_INPUT,
  plainExponentForm,
  primeFactorTotal,
} from "@/lib/tool-kit/math/primes";
import { layoutFactorTree } from "@/lib/tool-kit/math/tree-layout";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

/** English strings. A translated content file overrides these through `ui`. */
export const PRIME_FACTORIZATION_CALCULATOR_STRINGS = {
  number: "Whole number",
  numberHint: "Any whole number from 2 up to 10²⁰.",
  results: "Results",
  factorization: "Prime factorization",
  verdict: "Prime or composite?",
  prime: "Prime",
  composite: "Composite",
  divisors: "Number of divisors",
  distinct: "Distinct prime factors",
  total: "Prime factors, with repeats",
  expanded: "Written out",
  calculatorForm: "Calculator form",
  copyFactorization: "Copy factorization",
  copied: "Copied!",
  isPrime: "{n} is prime: its only divisors are 1 and itself.",
  treeHeading: "Factor tree",
  treeIntro:
    "Each composite number splits into the factor pair closest to its square root. The circled numbers at the ends of the branches are the prime factors.",
  treeTitle: "Factor tree of {n}",
  treeTooBig:
    "{n} has {count}, too many to draw as a tree. The division ladder below shows every step.",
  primeFactorsCount: "one: {count} prime factor|other: {count} prime factors",
  ladderHeading: "Division ladder",
  ladderIntro:
    "Divide by the smallest prime that goes in exactly, and repeat until the quotient is 1. The divisors you used are the prime factors.",
  divisorsHeading: "Divisors",
  divisorRule:
    "Add 1 to each exponent and multiply: {formula} = {count}. These are all of them:",
  divisorsTooMany:
    "Add 1 to each exponent and multiply: {formula} = {count}, too many to list here.",
  one: "1 is neither prime nor composite, and it has no prime factors.",
  zero: "0 has no prime factorization: every prime divides it.",
  negative:
    "Prime factorization is defined for positive whole numbers. A negative number factors as −1 times its absolute value: {n} = −1 × {factors}.",
  tooLarge: "Enter a whole number no larger than 10²⁰.",
  notWhole: "Enter a whole number, with no decimals, fractions or separators.",
  enterNumber: "Enter a whole number to factor it.",
  incomplete:
    "One part of this number resisted quick factoring, so the last factor shown may not be prime.",
} as const;

type Strings = typeof PRIME_FACTORIZATION_CALCULATOR_STRINGS;

const MAX_TREE_LEAVES = 24;
/** Spaces typed as digit grouping ("1 000 000") are dropped before reading. */
const SPACES = /\s+/g;
const MAX_LISTED_DIVISORS = 240;
const MONO_LINES =
  "space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed";

type Parsed =
  | { kind: "ok"; n: bigint; negative: boolean; factorization: Factorization }
  | { kind: "empty" | "invalid" | "zero" | "one" | "too-large" };

function readNumber(raw: string): Parsed {
  if (raw.trim() === "") {
    return { kind: "empty" };
  }
  const value = parseBigIntToken(raw.replace(SPACES, ""));
  if (value === null) {
    return { kind: "invalid" };
  }
  const n = value < BigInt(0) ? -value : value;
  if (n === BigInt(0)) {
    return { kind: "zero" };
  }
  if (n === BigInt(1)) {
    return { kind: "one" };
  }
  if (n > MAX_FACTOR_INPUT) {
    return { kind: "too-large" };
  }
  return {
    kind: "ok",
    n,
    negative: value < BigInt(0),
    factorization: factorize(n),
  };
}

function FactorTree({
  n,
  factors,
  strings,
}: {
  n: bigint;
  factors: Factorization["factors"];
  strings: Strings;
}) {
  const layout = useMemo(
    () => layoutFactorTree(buildFactorTree(factors)),
    [factors]
  );
  const byKey = new Map(layout.nodes.map((node) => [node.key, node]));
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-white p-2">
      <svg
        aria-label={t(strings.treeTitle, { n: String(n) })}
        fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
        height={layout.height}
        role="img"
        viewBox={`0 0 ${layout.width} ${layout.height}`}
        width={layout.width}
        xmlns="http://www.w3.org/2000/svg"
      >
        {layout.nodes.map((node) => {
          const parent = node.parentKey ? byKey.get(node.parentKey) : null;
          return parent ? (
            <line
              key={`edge-${node.key}`}
              stroke={CHART_INK.axis}
              x1={parent.x}
              x2={node.x}
              y1={parent.y + 11}
              y2={node.y - 13}
            />
          ) : null;
        })}
        {layout.nodes.map((node) => (
          <g key={node.key}>
            {node.leaf ? (
              <rect
                fill="#e8f1f8"
                height={26}
                rx={13}
                stroke={CHART_COLORS[0]}
                strokeWidth={1.5}
                width={node.width}
                x={node.x - node.width / 2}
                y={node.y - 13}
              />
            ) : null}
            <text
              dominantBaseline="central"
              fill={CHART_INK.text}
              fontSize={14}
              fontWeight={node.leaf ? 600 : 400}
              textAnchor="middle"
              x={node.x}
              y={node.y}
            >
              {node.label}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

function DivisorSection({
  factorization,
  strings,
}: {
  factorization: Factorization;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const count = divisorCount(factorization.factors);
  const formula = factorization.factors
    .map((f) => `(${f.exponent} + 1)`)
    .join("");
  const divisors = listDivisors(factorization.factors, MAX_LISTED_DIVISORS);
  const values = { formula, count: fmt(count) };
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.divisorsHeading}</h3>
      <p className="text-muted-foreground text-sm">
        {t(divisors ? strings.divisorRule : strings.divisorsTooMany, values)}
      </p>
      {divisors ? (
        <p className="break-words rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
          {divisors.map(String).join(", ")}
        </p>
      ) : null}
    </>
  );
}

function Results({
  parsed,
  strings,
}: {
  parsed: Extract<Parsed, { kind: "ok" }>;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const { n, factorization } = parsed;
  const factors = factorization.factors;
  const isPrime = factors.length === 1 && factors[0].exponent === 1;
  const leaves = primeFactorTotal(factors);
  const form = exponentForm(factors);
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.factorization} value={form} />
        <StatCard
          label={strings.verdict}
          value={isPrime ? strings.prime : strings.composite}
        />
        <StatCard label={strings.divisors} value={fmt(divisorCount(factors))} />
        <StatCard label={strings.distinct} value={fmt(factors.length)} />
        <StatCard label={strings.total} value={fmt(leaves)} />
      </StatGrid>
      {parsed.negative ? (
        <Notice>{t(strings.negative, { n: `−${n}`, factors: form })}</Notice>
      ) : null}
      {factorization.complete ? null : (
        <ErrorNotice>{strings.incomplete}</ErrorNotice>
      )}
      <div className={MONO_LINES}>
        <p>
          {String(n)} = {form}
        </p>
        <p>
          {strings.expanded}: {expandedForm(factors)}
        </p>
        <p>
          {strings.calculatorForm}: {plainExponentForm(factors)}
        </p>
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyFactorization}
        small
        text={`${n} = ${form}`}
      />
      {isPrime ? <Notice>{t(strings.isPrime, { n: String(n) })}</Notice> : null}
      {isPrime ? null : (
        <>
          <h3 className={WIDGET_H3}>{strings.treeHeading}</h3>
          {leaves <= MAX_TREE_LEAVES ? (
            <>
              <p className="text-muted-foreground text-sm">
                {strings.treeIntro}
              </p>
              <FactorTree factors={factors} n={n} strings={strings} />
            </>
          ) : (
            <Notice>
              {t(strings.treeTooBig, {
                n: String(n),
                count: tPlural(strings.primeFactorsCount, locale, leaves, {
                  count: fmt(leaves),
                }).trim(),
              })}
            </Notice>
          )}
          <h3 className={WIDGET_H3}>{strings.ladderHeading}</h3>
          <p className="text-muted-foreground text-sm">{strings.ladderIntro}</p>
          <div className={MONO_LINES}>
            {divisionLadder(n, factors).map((step) => (
              <p key={String(step.dividend)}>
                {String(step.dividend)} ÷ {String(step.divisor)} ={" "}
                {String(step.quotient)}
              </p>
            ))}
          </div>
        </>
      )}
      <DivisorSection factorization={factorization} strings={strings} />
    </>
  );
}

function Outcome({ raw, strings }: { raw: string; strings: Strings }) {
  const parsed = useMemo(() => readNumber(raw), [raw]);
  switch (parsed.kind) {
    case "ok":
      return <Results parsed={parsed} strings={strings} />;
    case "empty":
      return <Notice>{strings.enterNumber}</Notice>;
    case "one":
      return <Notice>{strings.one}</Notice>;
    case "zero":
      return <Notice>{strings.zero}</Notice>;
    case "too-large":
      return <ErrorNotice>{strings.tooLarge}</ErrorNotice>;
    default:
      return <ErrorNotice>{strings.notWhole}</ErrorNotice>;
  }
}

export default function PrimeFactorizationCalculatorWidget() {
  const strings = useToolUi(PRIME_FACTORIZATION_CALCULATOR_STRINGS);
  const [raw, setRaw] = useState("360");
  // Factoring a 20-digit number can take a few dozen milliseconds; deferring keeps
  // typing responsive while it runs.
  const deferred = useDeferredValue(raw);
  return (
    <>
      <TextField
        hint={strings.numberHint}
        id="pf-number"
        label={strings.number}
        mono
        numeric
        onChange={setRaw}
        value={raw}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome raw={deferred} strings={strings} />
      </section>
    </>
  );
}
